import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Edit2, Trash2, Shield, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";

// Etiquetas display de módulos (sobreescriben capitalize CSS para acrónimos)
const MODULE_LABELS = {
  rrhh: "RRHH",
  sri: "SRI",
};
const moduleLabel = (m) => MODULE_LABELS[m] || (m ? m.charAt(0).toUpperCase() + m.slice(1) : "");
const ROLE_LABELS = { rrhh: "RRHH" };
const roleLabel = (r) => ROLE_LABELS[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : "");

/**
 * Devuelve dependencias faltantes para los submódulos asignados.
 * Cada elemento: { from: "modulo.submodulo", missing: {modulo, submodulo, razon} }
 */
function computeMissingDeps(form, meta) {
  const deps = meta.dependencias || {};
  const missing = [];
  for (const m of form.modulos || []) {
    const cur = form.permisos[m] || { submodulos: [] };
    for (const s of cur.submodulos || []) {
      const depKey = `${m}.${s}`;
      const required = deps[depKey] || [];
      for (const r of required) {
        // Verificar si el módulo dep está habilitado
        const hasModulo = (form.modulos || []).includes(r.modulo);
        if (!hasModulo) {
          missing.push({ from: `${m} · ${s}`, missing: r, type: "modulo" });
          continue;
        }
        // Si tiene módulo, verificar submodulo (si está restringido)
        const depSubmodulos = (form.permisos[r.modulo] || {}).submodulos || [];
        if (r.submodulo && depSubmodulos.length > 0 && !depSubmodulos.includes(r.submodulo)) {
          missing.push({ from: `${m} · ${s}`, missing: r, type: "submodulo" });
        }
      }
    }
  }
  return missing;
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ modulos: [], roles: [], acciones: [], submodulos: {}, dependencias: {} });
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [expanded, setExpanded] = useState({});

  function emptyForm() {
    return {
      email: "", password: "", nombres: "", apellidos: "",
      cargo: "", telefono: "", role: "ventas",
      modulos: [], permisos: {}, activo: true,
      pin_gerencia: "",
      ver_todos_clientes: false,
    };
  }

  const load = async () => {
    try {
      const [u, m] = await Promise.all([api.get("/users"), api.get("/users/meta")]);
      setUsers(u.data);
      setMeta(m.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setExpanded({}); setDialogOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    // Normalizar permisos: si vienen como lista de acciones, convertirlas al formato nuevo
    const permisosNorm = {};
    Object.entries(u.permisos || {}).forEach(([mod, val]) => {
      if (Array.isArray(val)) permisosNorm[mod] = { acciones: val, submodulos: [] };
      else if (val && typeof val === "object") permisosNorm[mod] = { acciones: val.acciones || [], submodulos: val.submodulos || [] };
    });
    setForm({
      email: u.email, password: "", nombres: u.nombres, apellidos: u.apellidos,
      cargo: u.cargo || "", telefono: u.telefono || "", role: u.role,
      modulos: u.modulos || [], permisos: permisosNorm, activo: u.activo,
      pin_gerencia: "",  // por seguridad, siempre se ingresa de nuevo
      _tiene_pin: !!u.tiene_pin_gerencia,
      ver_todos_clientes: !!u.ver_todos_clientes,
    });
    setExpanded({});
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      // limpiar permisos: sólo módulos habilitados
      const cleanPermisos = {};
      Object.keys(form.permisos || {}).forEach((mod) => {
        if (form.modulos.includes(mod)) cleanPermisos[mod] = form.permisos[mod];
      });
      const payload = { ...form, permisos: cleanPermisos };
      delete payload._tiene_pin;
      // Solo enviar pin_gerencia si el usuario lo tipeó (string no vacío) o explicitamente lo borra
      // Si el campo está vacío y está editando, lo dejamos sin tocar (no se envía la clave)
      if (payload.pin_gerencia === "") delete payload.pin_gerencia;
      if (editing) {
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Usuario creado");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar usuario ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleModulo = (m) => {
    setForm((f) => {
      const has = f.modulos.includes(m);
      const nuevoModulos = has ? f.modulos.filter((x) => x !== m) : [...f.modulos, m];
      const nuevoPerm = { ...(f.permisos || {}) };
      if (has) {
        delete nuevoPerm[m];
      } else if (!nuevoPerm[m]) {
        nuevoPerm[m] = { acciones: ["ver"], submodulos: [] };
      }
      return { ...f, modulos: nuevoModulos, permisos: nuevoPerm };
    });
  };

  const toggleAccion = (mod, accion) => {
    setForm((f) => {
      const cur = f.permisos[mod] || { acciones: [], submodulos: [] };
      const acciones = cur.acciones || [];
      const nueva = acciones.includes(accion) ? acciones.filter((a) => a !== accion) : [...acciones, accion];
      return { ...f, permisos: { ...f.permisos, [mod]: { ...cur, acciones: nueva } } };
    });
  };

  const toggleSubmodulo = (mod, sub) => {
    setForm((f) => {
      const cur = f.permisos[mod] || { acciones: [], submodulos: [] };
      const subs = cur.submodulos || [];
      const nuevo = subs.includes(sub) ? subs.filter((s) => s !== sub) : [...subs, sub];
      return { ...f, permisos: { ...f.permisos, [mod]: { ...cur, submodulos: nuevo } } };
    });
  };

  const toggleExpand = (m) => setExpanded((e) => ({ ...e, [m]: !e[m] }));

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Seguridad</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Usuarios</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Asigne módulos, acciones y submódulos específicos por usuario.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-user-btn">
          <UserPlus size={16} className="mr-2" /> Nuevo usuario
        </Button>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Nombre</th><th>Email</th><th>Rol</th><th>Cargo</th><th>Módulos</th><th>Estado</th><th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="font-medium">{u.nombres} {u.apellidos}</td>
                <td className="text-[#4a5c53]">{u.email}</td>
                <td><Badge variant="outline">{roleLabel(u.role)}</Badge></td>
                <td className="text-[#4a5c53]">{u.cargo || "—"}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {u.role === "admin" ? (
                      <Badge className="bg-[#205c45] text-white"><Shield size={10} className="mr-1" />Todos</Badge>
                    ) : (u.modulos?.length ? u.modulos.map((m) => {
                      const subs = u.permisos?.[m]?.submodulos || [];
                      return (
                        <Badge key={m} variant="secondary" className="text-[10px]">
                          {m}{subs.length > 0 ? ` (${subs.length} sub)` : ""}
                        </Badge>
                      );
                    }) : <span className="text-xs text-[#8ba397]">Ninguno</span>)}
                  </div>
                </td>
                <td>{u.activo ? <Badge className="fefo-safe border">Activo</Badge> : <Badge className="fefo-vencido border">Inactivo</Badge>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Edit2 size={16} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}><Trash2 size={16} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin usuarios aún</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Nombres</Label><Input value={form.nombres} onChange={(e) => setForm({ ...form, nombres: e.target.value })} data-testid="user-nombres-input" /></div>
            <div className="space-y-1.5"><Label>Apellidos</Label><Input value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} data-testid="user-apellidos-input" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div className="space-y-1.5"><Label>Contraseña {editing && <span className="text-xs text-[#8ba397]">(opcional)</span>}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.roles.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Cargo</Label><Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>

            {(form.role === "gerencia" || form.role === "admin") && (
              <div className="space-y-1.5 col-span-2">
                <Label>
                  PIN Gerencia (4-6 dígitos)
                  {form._tiene_pin && <span className="ml-2 text-xs text-[#205c45]">✓ PIN actual configurado · ingrese uno nuevo para reemplazar</span>}
                </Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={form.pin_gerencia}
                  onChange={(e) => setForm({ ...form, pin_gerencia: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  placeholder={form._tiene_pin ? "•••• (dejar vacío para no cambiar)" : "Ej: 9988"}
                  data-testid="user-pin-input"
                  className="font-mono tracking-[0.4em]"
                />
                <p className="text-[10px] text-[#5c7368] italic">El PIN autoriza ventas por debajo de precio_minimo. Solo usuarios Gerencia/Admin pueden tenerlo.</p>
              </div>
            )}

            <div className="col-span-2 space-y-2">
              <Label>Permisos por módulo, acción y submódulo</Label>
              <p className="text-xs text-[#5c7368]">Habilite el módulo, expanda para asignar acciones específicas y submódulos. Si no marca submódulos, el usuario tendrá acceso a TODOS dentro del módulo.</p>

              {/* Aviso de dependencias faltantes */}
              {(() => {
                const missing = computeMissingDeps(form, meta);
                if (missing.length === 0) return null;
                return (
                  <div className="bg-[#fef7ec] border border-[#c8943f] rounded-md p-3 space-y-1.5" data-testid="user-deps-warning">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={16} className="text-[#c8943f] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-[#8a5500] mb-1">
                          Dependencias faltantes ({missing.length})
                        </p>
                        <ul className="text-[11px] text-[#8a5500] space-y-1 list-disc list-inside">
                          {missing.map((m, i) => (
                            <li key={i}>
                              <strong>{m.from}</strong> requiere{" "}
                              <strong>{m.missing.modulo}{m.missing.submodulo ? ` · ${m.missing.submodulo.replace(/_/g, " ")}` : ""}</strong>
                              {" — "}<span className="italic">{m.missing.razon}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="border border-[#d1dbd6] rounded-md divide-y divide-[#e6ebe8]">
                {meta.modulos.map((m) => {
                  const enabled = form.modulos.includes(m);
                  const submodulosDisponibles = meta.submodulos?.[m] || [];
                  const cur = form.permisos[m] || { acciones: [], submodulos: [] };
                  return (
                    <div key={m} className="bg-white">
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Checkbox checked={enabled} onCheckedChange={() => toggleModulo(m)} data-testid={`mod-${m}`} />
                        <button type="button" onClick={() => enabled && toggleExpand(m)} className={`flex items-center gap-1 font-medium flex-1 text-left ${enabled ? "cursor-pointer text-[#15241d]" : "text-[#8ba397]"}`} data-testid={`expand-${m}`}>
                          {enabled ? (expanded[m] ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-[14px]" />}
                          {moduleLabel(m)}
                        </button>
                        {enabled && (
                          <div className="flex gap-1">
                            {(cur.acciones || []).map((a) => <Badge key={a} variant="outline" className="text-[10px] capitalize">{a}</Badge>)}
                            {(cur.submodulos || []).length > 0 && <Badge className="text-[10px] bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]">{cur.submodulos.length} sub</Badge>}
                          </div>
                        )}
                      </div>
                      {enabled && expanded[m] && (
                        <div className="px-8 py-3 bg-[#f7f9f8] space-y-3">
                          <div>
                            <div className="text-xs font-semibold text-[#5c7368] mb-1.5">Acciones permitidas</div>
                            <div className="flex flex-wrap gap-3">
                              {meta.acciones.map((a) => (
                                <label key={a} className="flex items-center gap-1.5 text-sm capitalize cursor-pointer" data-testid={`act-${m}-${a}`}>
                                  <Checkbox checked={(cur.acciones || []).includes(a)} onCheckedChange={() => toggleAccion(m, a)} />
                                  {a}
                                </label>
                              ))}
                            </div>
                          </div>
                          {submodulosDisponibles.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-[#5c7368] mb-1.5">Submódulos {(cur.submodulos || []).length === 0 && <span className="text-[#8ba397] font-normal">(vacío = todos)</span>}</div>
                              <div className="grid grid-cols-3 gap-2">
                                {submodulosDisponibles.map((s) => (
                                  <label key={s} className="flex items-center gap-1.5 text-sm capitalize cursor-pointer" data-testid={`sub-${m}-${s}`}>
                                    <Checkbox checked={(cur.submodulos || []).includes(s)} onCheckedChange={() => toggleSubmodulo(m, s)} />
                                    {s.replace(/_/g, " ")}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="activo" checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: !!v })} />
              <Label htmlFor="activo">Usuario activo</Label>
            </div>

            <div className="col-span-2 rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="ver_todos_clientes"
                  checked={!!form.ver_todos_clientes}
                  onCheckedChange={(v) => setForm({ ...form, ver_todos_clientes: !!v })}
                  data-testid="ver-todos-clientes-check"
                />
                <div className="flex-1">
                  <Label htmlFor="ver_todos_clientes" className="font-semibold text-[#205c45] cursor-pointer">
                    Ver todos los clientes y ventas (visibilidad cruzada)
                  </Label>
                  <p className="text-[11px] text-[#4a5c53] mt-1 leading-tight">
                    Útil para <strong>secretaria/asistente de ventas</strong> que factura a clientes de cualquier vendedor,
                    y para el <strong>gerente de ventas</strong> que necesita ver los reportes de todos.
                    Al guardar facturas/cotizaciones/notas, se conserva el vendedor original del cliente (las comisiones no cambian).
                    Sin esta opción, los vendedores siguen viendo sólo sus clientes (default).
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="save-user-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
